import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import apiClient from "../../api/apiClient";
import API_CONFIG from "../../config/api";
import { useAuth } from "../../context/AuthContext";
import { useSelection } from "../../SelectionContext";
import useBusinessResource from "../../hooks/useBusinessResource";
import { BusinessScope, invalidateBusinessResources } from "../../utils/businessResourceFreshness";
import { useQueryClient } from "@tanstack/react-query";
import { buildCacheKey, fetchWithCacheMetadata } from "../../utils/offlineCache";
import { isInvoiceSubjectEnabled } from "../../utils/invoiceSubject";

export type FormOrderType = "sales" | "proforma" | "purchase" | "purchase_order";
export interface OrderChoiceProduct {
  id: number; id_produit: number; item_type: "product" | "service";
  product_id: number | null; service_id: number | null; name: string; nom_produit: string;
  prix_vente: number; sales_price?: number | null; purchase_price?: number; current_stock?: number;
  quantite_stock?: number; stock_management_mode?: "standard" | "pre_order" | "unlimited";
  product_type?: string; production_kind?: string | null; sellable_without_production?: boolean;
  unit_id?: number | null; unit_name?: string; unit_short_name?: string; code_barre?: string;
  duration_minutes?: number | null; purchase_packaging_profiles?: any[];
  image?: string; category_id?: number | null; code?: string; barcode_value?: string | null;
}
export interface OrderFormOptions {
  entities?: Array<{ id: number; name: string }>;
  selectedEntity?: { id: number; name: string } | null;
  products?: OrderChoiceProduct[];
  taxes?: any[]; units?: any[]; hasMore?: boolean; page?: number;
  invoice_subject_enabled?: boolean;
  categories?: Array<{ id: number; name: string }>;
  purchaseOrders?: any[];
  selectedPurchaseOrder?: any;
  freshness?: "verified" | "cached";
}

export const choiceItemId = (item: any) => item.item_type === "service"
  ? -Number(item.service_id || Math.abs(item.produit_id || item.id_produit))
  : Number(item.product_id || item.produit_id || item.id_produit || item.id);

export async function fetchOrderFormOptions(
  scope: BusinessScope, orderType: FormOrderType | "pos", action: "create" | "edit",
  parameters: Record<string, unknown>, signal?: AbortSignal,
): Promise<OrderFormOptions> {
  const expected = parameters.kind === "products" ? "products" : parameters.kind === "references" ? "taxes"
    : parameters.kind === "purchase-orders" ? "purchaseOrders" : parameters.kind === "categories" ? "categories" : "entities";
  const fetchFresh = async () => {
    const response = await apiClient.get(`${API_CONFIG.BASE_URL}/api/orders/form-options`, {
      params: { company_id: scope.companyId, warehouse_id: scope.warehouseId, order_type: orderType, action, ...parameters },
      headers: { "X-Company-Id": String(scope.companyId) }, signal, skipGlobalLoading: true,
    } as any);
    if (!response.data || !Array.isArray(response.data[expected]) || (expected === "taxes" && !Array.isArray(response.data.units))) {
      throw new Error("Réponse de choix de formulaire invalide.");
    }
    return response.data;
  };
  // Sales already support queued offline writes. Persist only the bounded choice projection in the existing scoped store.
  const result = ["sales", "pos"].includes(orderType) ? await fetchWithCacheMetadata(buildCacheKey("order-form-choices", {
    userId: scope.userId, companyId: scope.companyId, warehouseId: scope.warehouseId, orderType, action, ...parameters,
  }), fetchFresh) : { data: await fetchFresh(), status: "verified" };
  const data = result.data;
  if (!data || !Array.isArray(data[expected]) || (expected === "taxes" && !Array.isArray(data.units))) {
    throw new Error("Choix de formulaire indisponibles dans ce contexte. Reconnectez-vous pour les charger.");
  }
  if (data.products) data.products = data.products.map((item: any) => ({ ...item,
    id: Number(item.id), id_produit: Number(item.id),
    product_id: item.item_type === "service" ? null : Number(item.id),
    service_id: item.item_type === "service" ? Math.abs(Number(item.id)) : null,
    nom_produit: item.name, prix_vente: Number(item.sales_price || 0),
    purchase_price: item.purchase_price == null ? undefined : Number(item.purchase_price),
    current_stock: item.current_stock == null ? undefined : Number(item.current_stock),
    quantite_stock: item.current_stock == null ? undefined : Number(item.current_stock),
    code_barre: item.barcode_value || item.code,
    image: item.image || undefined,
    sellable_without_production: item.sellable_without_production === true || Number(item.sellable_without_production) === 1,
  }));
  return { ...data, freshness: result.status === "cached" ? "cached" : "verified" };
}

export function useOrderChoiceScope() {
  const { user } = useAuth();
  const { selectedCompany, selectedWarehouse } = useSelection();
  const userId = user?.id;
  const companyId = (selectedCompany as any)?.id ?? selectedCompany;
  const warehouseId = (selectedWarehouse as any)?.id ?? selectedWarehouse;
  return useMemo(() => ({ userId, companyId, warehouseId, moduleScope: "commercial" }), [userId, companyId, warehouseId]);
}

export function useOrderChoiceResource({ orderType, action, active, enabled = true, parameters } : {
  orderType: FormOrderType; action: "create" | "edit"; active: boolean; enabled?: boolean;
  parameters: Record<string, unknown>;
}) {
  const scope = useOrderChoiceScope();
  const resource = useBusinessResource<OrderFormOptions>({ ...scope,
    resource: ["sales", "proforma"].includes(orderType) ? "sales-form-options" : "purchase-form-options",
    parameters: [orderType, action, parameters], enabled: enabled && active, active,
    networkMode: orderType === "sales" ? "always" : "online",
    load: (signal) => fetchOrderFormOptions(scope, orderType, action, parameters, signal),
  });
  return { ...resource, status: resource.data?.freshness === "cached" ? "cached" as const : resource.status,
    error: resource.error || (resource.data?.freshness === "cached" ? new Error("Les choix proviennent du cache hors ligne et doivent être revalidés.") : null) };
}

export default function useOrderFormOptions({ orderType, action, active, enabled, entityId, items } : {
  orderType: FormOrderType; action: "create" | "edit"; active: boolean; enabled: boolean;
  entityId?: number | string | null; items: any[];
}) {
  const scope = useOrderChoiceScope();
  const client = useQueryClient();
  const contextKey = JSON.stringify([scope.userId, scope.companyId, scope.warehouseId, orderType]);
  const currentContext = useRef({ key: contextKey, active });
  currentContext.current = { key: contextKey, active };
  useEffect(() => () => { currentContext.current.active = false; }, []);
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [page, setPage] = useState(1);
  useEffect(() => {
    const timer = setTimeout(() => { setAppliedSearch(search.trim()); setPage(1); }, 300);
    return () => clearTimeout(timer);
  }, [search]);
  useEffect(() => { setSearch(""); setAppliedSearch(""); setPage(1); }, [contextKey, active]);
  const entities = useOrderChoiceResource({ orderType, action, active, enabled,
    parameters: { kind: "entities", search: appliedSearch, page, limit: 50,
      ...(entityId ? { selected_id: entityId } : {}) } });
  const references = useOrderChoiceResource({ orderType, action, active, enabled, parameters: { kind: "references" } });
  const ids = Array.from(new Set(items.map(choiceItemId).filter((id) => Number.isSafeInteger(id) && id !== 0))).sort((a, b) => a - b);
  const idsText = ids.join(",");
  const products = useBusinessResource<OrderFormOptions>({ ...scope,
    resource: ["sales", "proforma"].includes(orderType) ? "sales-form-options" : "purchase-form-options",
    parameters: [orderType, action, "selected-products", idsText], enabled: enabled && active && ids.length > 0, active,
    networkMode: orderType === "sales" ? "always" : "online",
    load: async (signal) => {
      const result: OrderChoiceProduct[] = [];
      let cached = false;
      // A large document is verified in bounded batches, never by loading the entire catalogue.
      for (let offset = 0; offset < ids.length; offset += 100) {
        const data = await fetchOrderFormOptions(scope, orderType, action,
          { kind: "products", item_ids: ids.slice(offset, offset + 100).join(",") }, signal);
        result.push(...data.products!);
        cached = cached || data.freshness === "cached";
      }
      return { products: result, freshness: cached ? "cached" : "verified" };
    },
  });
  const entityChoices = useMemo(() => {
    const options = [...(entities.data?.entities || [])];
    if (entities.data?.selectedEntity && !options.some((item) => Number(item.id) === Number(entities.data!.selectedEntity!.id))) {
      options.unshift(entities.data.selectedEntity);
    }
    return options;
  }, [entities.data]);
  const missingEntity = Boolean(entityId && entities.status === "verified" && !entities.data?.selectedEntity);
  const missingProducts = products.status === "verified" && products.data?.freshness !== "cached"
    ? items.filter((item) => !products.data?.products?.some((product) => product.id === choiceItemId(item))) : [];
  const missingTaxes = references.status === "verified" ? items.filter((item) => item.taxe &&
    !references.data?.taxes?.some((tax) => Number(tax.id) === Number(item.taxe))) : [];
  const packagingChanged = (item: any, currentProducts: OrderChoiceProduct[]) => {
    if (item.purchase_entry_mode !== "packaging" || !item.selected_purchase_packaging_id) return false;
    const oldProfile = item.purchase_packaging_profiles?.find((profile: any) => Number(profile.id) === Number(item.selected_purchase_packaging_id));
    const currentProfile = currentProducts.find((product) => product.id === choiceItemId(item))?.purchase_packaging_profiles
      ?.find((profile: any) => Number(profile.id) === Number(item.selected_purchase_packaging_id));
    return !currentProfile || Number(currentProfile.contained_quantity) !== Number(oldProfile?.contained_quantity);
  };
  const changedPackagings = products.status === "verified" && products.data?.freshness !== "cached" ? items.filter((item) => packagingChanged(item, products.data?.products || [])) : [];
  const assertContext = () => {
    if (currentContext.current.key !== contextKey || !currentContext.current.active) {
      throw new Error("Le contexte a changé. Rouvrez le formulaire.");
    }
  };
  const verify = async (selectedEntity: number, selectedItems: any[], options?: { invoiceSubjectChanged?: boolean }) => {
    assertContext();
    const capturedContext = contextKey;
    const currentEntities = await entities.refresh({ force: true });
    const confirmedReferences = await references.refresh({ force: true });
    const confirmedEntity = Number(entityId) === selectedEntity ? currentEntities
      : await fetchOrderFormOptions(scope, orderType, action, { kind: "entities", selected_id: selectedEntity, limit: 1 });
    if (confirmedEntity.freshness === "cached" || confirmedReferences.freshness === "cached") {
      throw new Error("Les références n’ont pas pu être revalidées en ligne. Votre saisie est conservée ; réessayez.");
    }
    if (currentContext.current.key !== capturedContext || !currentContext.current.active) throw new Error("Le contexte a changé. Rouvrez le formulaire.");
    if (orderType === "sales" && options?.invoiceSubjectChanged && !isInvoiceSubjectEnabled(confirmedReferences)) {
      throw new Error("La saisie de l’objet est désactivée pour cet établissement. Votre saisie est conservée ; réactivez l’option ou rétablissez l’objet initial avant d’enregistrer.");
    }
    if (!confirmedEntity.selectedEntity) throw new Error("Le client ou fournisseur n’est plus disponible. Votre saisie est conservée ; choisissez un tiers disponible.");
    const selectedIds = Array.from(new Set(selectedItems.map(choiceItemId))).sort((a, b) => a - b);
    const confirmed: OrderChoiceProduct[] = [];
    if (selectedIds.length && selectedIds.join(",") === idsText) {
      const data = await products.refresh({ force: true });
      if (data.freshness === "cached") throw new Error("Les articles n’ont pas pu être revalidés en ligne. Votre saisie est conservée ; réessayez.");
      confirmed.push(...data.products!);
    } else for (let offset = 0; offset < selectedIds.length; offset += 100) {
      const data = await fetchOrderFormOptions(scope, orderType, action,
        { kind: "products", item_ids: selectedIds.slice(offset, offset + 100).join(",") });
      if (data.freshness === "cached") throw new Error("Les articles n’ont pas pu être revalidés en ligne. Votre saisie est conservée ; réessayez.");
      confirmed.push(...data.products!);
    }
    if (currentContext.current.key !== capturedContext || !currentContext.current.active) throw new Error("Le contexte a changé. Rouvrez le formulaire.");
    const missing = selectedItems.find((item) => !confirmed.some((product) => product.id === choiceItemId(item)));
    if (missing) throw new Error(`L’article « ${missing.nom_produit || missing.name || choiceItemId(missing)} » n’est plus disponible. Votre saisie est conservée.`);
    if (selectedItems.some((item) => item.taxe && !confirmedReferences.taxes?.some((tax) => Number(tax.id) === Number(item.taxe)))) {
      throw new Error("Une taxe sélectionnée n’est plus disponible. Votre saisie est conservée ; corrigez la taxe avant d’enregistrer.");
    }
    if (selectedItems.some((item) => packagingChanged(item, confirmed))) {
      throw new Error("Un conditionnement d’achat a changé. Votre saisie est conservée ; choisissez un conditionnement actuel ou une quantité directe.");
    }
    return confirmed;
  };
  const invalidate = useCallback(() => invalidateBusinessResources(client, scope,
    orderType === "sales" ? "sale" : "purchase"), [client, scope, orderType]);
  const invalidateEntity = useCallback(() => invalidateBusinessResources(client, scope,
    orderType === "sales" ? "customer" : "supplier"), [client, scope, orderType]);
  const loading = entities.loading || references.loading || products.loading;
  const error = entities.error || references.error || products.error || (products.data?.freshness === "cached" ? new Error("Les articles doivent être revalidés en ligne.") : null);
  const refresh = async () => { await Promise.all([entities.refresh(), references.refresh(), ...(ids.length ? [products.refresh()] : [])]); };
  return { entities: entityChoices, references: references.data, products: products.data?.products || [],
    entityResource: entities, productsResource: products, search, setSearch, page, setPage,
    hasMore: Boolean(entities.data?.hasMore), missingEntity, missingProducts, missingTaxes, changedPackagings, verify, assertContext, invalidate, invalidateEntity, scope,
    ready: enabled && entities.status === "verified" && references.status === "verified" &&
      (!ids.length || (products.status === "verified" && products.data?.freshness !== "cached")),
    notice: { data: entities.data || references.data || products.data, loading, error, refresh },
  };
}
